// A header row's pieces drawn from the outside's data (shell/outside): the
// chips beside the title, the note and the buttons, each by its own `render`
// when it has one, else the data's plain form. `DetailHeader` and
// `PanelHeader` draw a header given as data with these.
import { Fragment, type ReactNode } from "react";
import { IconButton } from "../ui/button";
import { Icon, type IconKind } from "../ui/icon";
import { MenuButton } from "../ui/menu";
import type { ShellActionInput, ShellHeaderInput } from "./outside/outside";

/** The chips beside the title, from the header's data. */
export function chipsOf(header: ShellHeaderInput, narrow: boolean): ReactNode {
  return (
    <>
      {header.chips?.map((c) => (
        <Fragment key={c.id}>
          {c.render ? (
            c.render(narrow)
          ) : (
            <button type="button" className={HEADER_PILL} onClick={c.onPress}>
              {c.icon && <Icon kind={c.icon as IconKind} size={13} />}
              <span className="truncate">{c.label}</span>
            </button>
          )}
        </Fragment>
      ))}
    </>
  );
}

/** The note and the buttons, from the header's data. */
export function actionsOf(
  header: ShellHeaderInput,
  narrow: boolean,
): ReactNode {
  return (
    <>
      {!narrow && header.note && (
        <span className="truncate text-[12px] text-muted">{header.note}</span>
      )}
      {header.actions?.map((a) => (
        <Fragment key={a.id}>
          {a.render ? a.render(narrow) : <DataButton action={a} />}
        </Fragment>
      ))}
    </>
  );
}

/** A header button drawn from its data alone (no `render`). */
function DataButton({ action: a }: { action: ShellActionInput }) {
  const glyph = <Icon kind={(a.icon ?? "more") as IconKind} size={15} />;
  if (a.items)
    return (
      <MenuButton
        label={a.label}
        align="end"
        className={HEADER_ICON_BUTTON}
        items={a.items.map((i) =>
          "type" in i
            ? ("separator" as const)
            : {
                id: i.id,
                label: i.label,
                onSelect: () => i.onPress?.(),
                ...(i.danger ? { danger: true } : {}),
                ...(i.disabled ? { disabled: true } : {}),
              },
        )}
      >
        {glyph}
      </MenuButton>
    );
  if (a.style === "pill")
    return (
      <button
        type="button"
        className={HEADER_PILL}
        disabled={a.disabled}
        onClick={() => a.onPress?.()}
      >
        {a.label}
      </button>
    );
  return (
    <IconButton
      label={a.label}
      aria-pressed={a.on}
      disabled={a.disabled}
      onClick={() => a.onPress?.()}
    >
      {glyph}
    </IconButton>
  );
}

/** A small button with its words in a header row ("Done"). */
export const HEADER_PILL =
  "inline-flex h-[26px] max-w-full flex-none cursor-pointer items-center gap-[6px] rounded-[13px] border border-pill-border bg-surface px-[12px] text-[11.5px] text-body-soft hover:border-field-strong hover:text-body";

/** A header row's round icon button that opens a menu ("..."). */
export const HEADER_ICON_BUTTON =
  "inline-flex h-[26px] w-[26px] flex-none cursor-pointer items-center justify-center rounded-[13px] text-muted hover:bg-hover hover:text-body";
